class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    this.poster = this.querySelector(".video-wrapper");
    this.template = this.querySelector("template");
    this.hasPoster = !!this.querySelector(".image_container");

    if (this.hasPoster) {
      // Poster exists → load on click
      this.poster.addEventListener("click", () => this.loadContent());
    } else {
      // No poster → load when visible
      this.setupIntersectionObserver();
    }
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadContent();
            observer.unobserve(this);
          }
        });
      },
      {
        threshold: 0,
        rootMargin: "200px 0px" // load 200px before entering viewport
      }
    );

    observer.observe(this);
  }

  loadContent() {
    if ((this.hasAttribute("loaded") && this.querySelector("video")) || !this.template) return;

    // Clone video from template
    const content = this.template.content.cloneNode(true);

    // Hide poster image if exists
    const imageContainer = this.querySelector(".image_container");
    if (imageContainer) {
      imageContainer.style.display = "none";
    }

    // Append video
    (this.poster || this).appendChild(content);

    this.setAttribute("loaded", "true");

    // Autoplay safety
    const video = this.querySelector("video");
    if (video?.autoplay) {
      video.play().catch(() => {});
    }
  }
}

if (!customElements.get("deferred-media")) {
  customElements.define("deferred-media", DeferredMedia);
}

// Read More Custom Component
class ReadMore extends HTMLElement {
  connectedCallback() {
    this.text = this.querySelector(".block_description_text");
    this.toggle = this.querySelector(".read_more_inline");
    this.expanded = false;
    
    if (!this.text || !this.toggle) return;

    const styles = getComputedStyle(this.text);
    this.lineHeight = parseFloat(styles.lineHeight);
    this.collapsedHeight = this.lineHeight * 2;
    
    // Initial collapsed state
    this.text.style.maxHeight = this.collapsedHeight + "px";
    
    requestAnimationFrame(() => {
      if (this.text.scrollHeight <= this.collapsedHeight + 2) {
        this.toggle.style.display = "none";
      }
    });

    this.toggle.addEventListener("click", () => this.toggleContent());
  }

  toggleContent() {
    this.expanded = !this.expanded;
    this.classList.toggle("expanded", this.expanded);

    if (this.expanded) {
      this.text.style.maxHeight = this.text.scrollHeight + "px";
      this.toggle.textContent = "Read less";
    } else {
      this.text.style.maxHeight = this.collapsedHeight + "px";
      this.toggle.textContent = "Read more";
    }
  }
}

if (!customElements.get("read-more")) {
  customElements.define("read-more", ReadMore);
}

// Play Video on Hover & Active Slide Sync Handler
class CustomMediaVideoManager {
  constructor(carouselEl) {
    this.carouselEl = carouselEl;
    this.hoverPlayOnly = carouselEl.dataset.playOnHover === 'true';
    this.isMobile = window.matchMedia('(max-width: 749px), (pointer: coarse)').matches;
    this._hoveredSlide = null;
    this.init();
  }

  init() {
    this.setupHoverEvents();
    this.setupCarouselEvents();
    this.setupVisibilityObserver();

    const mediaQuery = window.matchMedia('(max-width: 749px), (pointer: coarse)');
    mediaQuery.addEventListener('change', (e) => {
      this.isMobile = e.matches;
      this.syncSlideVideos();
    });
  }

  isSlideVisibleInCarousel(slide) {
    if (slide.classList.contains('is-active') || slide.classList.contains('is-visible')) {
      return true;
    }
    const rect = slide.getBoundingClientRect();
    const carouselRect = this.carouselEl.getBoundingClientRect();
    return (
      rect.width > 0 &&
      rect.right > carouselRect.left + 10 &&
      rect.left < carouselRect.right - 10
    );
  }

  setupHoverEvents() {
    this.carouselEl.addEventListener('mouseover', (e) => {
      const slide = e.target.closest('.splide__slide');
      if (!slide) return;

      if (this._hoveredSlide !== slide) {
        this._hoveredSlide = slide;

        // Pause carousel autoplay on hover so slide doesn't slide away
        if (this.carouselEl.carousel?.Components?.Autoplay) {
          this.carouselEl.carousel.Components.Autoplay.pause();
        }

        // If deferred media is present, load video on hover
        const deferredMedia = slide.querySelector('deferred-media');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
          deferredMedia.loadContent();
        }

        // Handle video play on hover
        const video = slide.querySelector('.video-wrapper video') || slide.querySelector('video');
        if (video) {
          video.play().catch(() => {});
        }
      }
    });

    this.carouselEl.addEventListener('mouseout', (e) => {
      const slide = e.target.closest('.splide__slide');
      if (!slide) return;

      const related = e.relatedTarget;
      if (related && slide.contains(related)) return;

      if (this._hoveredSlide === slide) {
        this._hoveredSlide = null;
      }

      // Resume carousel autoplay on unhover
      if (this.carouselEl.carousel?.Components?.Autoplay) {
        this.carouselEl.carousel.Components.Autoplay.play();
      }

      // If play-on-hover mode is enabled on desktop, pause video on mouseleave
      if (this.hoverPlayOnly && !this.isMobile) {
        const video = slide.querySelector('.video-wrapper video') || slide.querySelector('video');
        if (video) video.pause();
      }
    });
  }

  setupCarouselEvents() {
    const checkCarousel = () => {
      if (this.carouselEl.carousel) {
        const splide = this.carouselEl.carousel;

        splide.on('move moved active refresh', () => {
          this.syncSlideVideos();
        });

        this.syncSlideVideos();
      } else {
        setTimeout(checkCarousel, 50);
      }
    };
    checkCarousel();
  }

  syncSlideVideos() {
    const slides = Array.from(this.carouselEl.querySelectorAll('.splide__slide'));
    slides.forEach((slide) => {
      const video = slide.querySelector('.video-wrapper video') || slide.querySelector('video');
      if (!video) return;

      const isHovered = (this._hoveredSlide === slide);
      const isVisible = isHovered || this.isSlideVisibleInCarousel(slide);

      if (isVisible) {
        if (isHovered || !this.hoverPlayOnly || this.isMobile) {
          video.play().catch(() => {});
        }
      } else {
        video.pause();
      }
    });
  }

  pauseAllVideos() {
    const videos = this.carouselEl.querySelectorAll('.video-wrapper video, video');
    videos.forEach((video) => video.pause());
  }

  setupVisibilityObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            this.pauseAllVideos();
            if (this.carouselEl.carousel?.Components?.Autoplay) {
              this.carouselEl.carousel.Components.Autoplay.pause();
            }
          } else {
            this.syncSlideVideos();
            if (this.carouselEl.carousel?.Components?.Autoplay) {
              this.carouselEl.carousel.Components.Autoplay.play();
            }
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(this.carouselEl);
  }
}

function initCustomMediaVideoManagers() {
  const carousels = document.querySelectorAll('.custom_media carousel-component');
  carousels.forEach((carousel) => {
    if (!carousel._videoManager) {
      carousel._videoManager = new CustomMediaVideoManager(carousel);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomMediaVideoManagers);
} else {
  initCustomMediaVideoManagers();
}

document.addEventListener('shopify:section:load', (event) => {
  const carousels = event.target.querySelectorAll('.custom_media carousel-component');
  carousels.forEach((carousel) => {
    carousel._videoManager = new CustomMediaVideoManager(carousel);
  });
});